// src/components/shared/forms/HubspotApiForm.client.tsx
"use client";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { PortableText } from "@portabletext/react";
import CheckboxCard from "./CheckboxCard";
import "./hubspot-forms.css";
import { sendEvent } from "@dash0/sdk-web";
import { ensurePostHogInitialized } from "@/helpers/analytics";
import { NEWSLETTER_FORM_KEY } from "@/lib/hubspot/constants";
import { DEMO_FORMS, getDemoRequestProperties } from "@/lib/hubspot/demo-tracking";
import type {
 HubspotApiFormEmbed,
 HubspotSyncedField,
 HubspotApiSubmitPayload,
} from "@/types/hubspotForm";

declare global {
 interface Window {
  turnstile?: {
   render: (container: HTMLElement, options: {
    sitekey: string;
    action?: string;
    theme?: "auto" | "light" | "dark";
    size?: "normal" | "compact" | "flexible";
    appearance?: "always" | "execute" | "interaction-only";
    callback?: (token: string) => void;
    "expired-callback"?: () => void;
    "error-callback"?: () => void;
   }) => string;
   reset: (widgetId?: string) => void;
  };
 }
}

type Props = {
 embed: HubspotApiFormEmbed;
 pageUri?: string;
 pageName?: string;

 /**
  * Values the embedding page injects into the form's hidden HubSpot fields,
  * keyed by internal field name. Only fields the form actually hides are sent,
  * and these win over the field's own value source in the form definition.
  */
 hiddenFieldValues?: Record<string, string | number | null | undefined>;

 /** Optional: allow the embedding page to override wrapper styles */
 className?: string;
 /** Optional content directly below the submit button. */
 afterSubmitButton?: React.ReactNode;
 /** Render a multiple-checkbox field as cards, keyed by its HubSpot option values. */
 choiceCards?: Record<string, Record<string, { label: string; description?: string }>>;
 /** Notify the embedding page after a successful, non-redirecting submission. */
 onSuccess?: (fields: HubspotApiSubmitPayload["fields"]) => void;
};

type SubmitState = "idle" | "submitting" | "success" | "error";

/** Normalizes an optional form-definition value to a trimmed string. */
function cleanString(input?: string | null): string {
 return String(input ?? "").trim();
}

function decodeHtmlEntities(input: string): string {
 if (typeof document === "undefined") return input;
 const el = document.createElement("textarea");
 el.innerHTML = input;
 return el.value;
}

function stripHtmlTags(input: string): string {
 return input.replace(/<[^>]*>/g, "");
}

function getCookieValue(name: string): string | null {
 if (typeof document === "undefined") return null;
 const match = document.cookie
 .split(";")
 .map((c) => c.trim())
 .find((c) => c.startsWith(`${name}=`));
 if (!match) return null;
 return decodeURIComponent(match.substring(name.length + 1));
}

function normalizeBlockedDomains(list: string[] | undefined): string[] {
 return (list ?? [])
 .map((d) => cleanString(d).toLowerCase())
 .filter(Boolean);
}

function extractEmailDomain(email: string): string | null {
 const at = email.lastIndexOf("@");
 if (at <= 0) return null;
 return email.slice(at + 1).trim().toLowerCase();
}

function isDomainBlocked(domain: string, blocked: string[]) {
 return blocked.some((b) => domain === b || domain.endsWith(`.${b}`));
}

function getComputedValue(computedKey: string, geoCountry: string | null): string {
 if (typeof window === "undefined") return "";
 const key = (computedKey ?? "").trim().toLowerCase();

 if (key === "language") return navigator.language || "";
 if (key === "referrer") return document.referrer || "";
 if (key === "country") return geoCountry || "";

 return "";
}

function shouldRenderField(field: HubspotSyncedField) {
 if (field.hidden) return false;
 if (field.ui?.enabled === false) return false;
 if (field.type === "file") return false;
 return true;
}

function sanitizeDomId(input: string) {
 return String(input ?? "")
 .replace(/^drafts\./, "")
 .trim()
 .replace(/[^a-zA-Z0-9\-_]/g, "-")
 .replace(/-+/g, "-")
 .replace(/^-|-$/g, "");
}

function normalizePrivacyPlacement(raw?: string) {
 const cleaned = cleanString(raw).toLowerCase();

 if (cleaned === "belowbutton") return "belowButton";
 if (cleaned === "abovebutton") return "aboveButton";

 if (cleaned.includes("below") || cleaned.includes("after")) return "belowButton";
 return "aboveButton";
}

/* ----------------------------- Validation ----------------------------- */

function isEmptyValue(v: unknown): boolean {
 if (v === null || v === undefined) return true;
 if (typeof v === "string") return v.trim().length === 0;
 if (Array.isArray(v)) return v.length === 0;
 if (typeof v === "boolean") return v === false;
 return false;
}

function validateEmail(v: string): boolean {
 return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function validatePhone(v: string): boolean {
 if (/[a-zA-Z]/.test(v)) return false;
 const digits = v.replace(/[^\d]/g, "");
 return digits.length >= 7 && digits.length <= 15;
}

function validateDateYYYYMMDD(v: string): boolean {
 if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
 const [y, m, d] = v.split("-").map((x) => Number(x));
 if (!y || !m || !d) return false;
 const dt = new Date(Date.UTC(y, m - 1, d));
 return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function validateDateTimeLocal(v: string): boolean {
 if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v)) return false;
 const dt = new Date(v);
 return !Number.isNaN(dt.getTime());
}

function validateNumber(v: string): boolean {
 if (v.trim() === "") return true;
 return !Number.isNaN(Number(v));
}

function validateField(field: HubspotSyncedField, rawValue: unknown): string | null {
 const t = cleanString(field.type);
 const required = Boolean(field.required);

 if (required) {
 if (t === "multicheckbox") {
 const arr = Array.isArray(rawValue) ? rawValue : [];
 if (arr.length === 0) return "This field is required.";
 } else if (t === "checkbox") {
 if (rawValue !== true) return "This field is required.";
 } else {
 if (isEmptyValue(rawValue)) return "This field is required.";
 }
 }

 if (isEmptyValue(rawValue)) return null;

 const s = typeof rawValue === "string" ? rawValue.trim() : String(rawValue ?? "").trim();

 if (t === "email" && !validateEmail(s)) {
 return "Please enter a valid email address.";
 }

 if (t === "phone" && !validatePhone(s)) {
 return "Please enter a valid phone number.";
 }

 if (t === "date" && !validateDateYYYYMMDD(s)) {
 return "Please enter a valid date.";
 }

 if (t === "datetime" && !validateDateTimeLocal(s)) {
 return "Please enter a valid date and time.";
 }

 if (t === "number" && !validateNumber(s)) {
 return "Please enter a valid number.";
 }

 return null;
}

/* ----------------------------- Component ----------------------------- */

type Form = NonNullable<HubspotApiFormEmbed["formRef"]>;

type InnerProps = Props & {
 form: Form;
};

export default function HubspotApiFormClient(props: Props) {
 const form = props.embed?.formRef;
 if (!form) return null;
 return <HubspotApiFormClientInner {...props} form={form} />;
}

type SelectOption = {
 value: string;
 label: string;
};

type SearchableSelectFieldProps = {
 id: string;
 name: string;
 label: string;
 required?: boolean;
 value: string;
 options: SelectOption[];
 helpText?: string;
 errMsg?: string | null;
 errorId: string;
 onChange: (value: string) => void;
 onBlur: () => void;
};

function SearchableSelectField({
 id,
 name,
 label,
 required,
 value,
 options,
 helpText,
 errMsg,
 errorId,
 onChange,
 onBlur,
}: SearchableSelectFieldProps) {
 const [open, setOpen] = useState(false);
 const [query, setQuery] = useState("");
 const rootRef = useRef<HTMLDivElement | null>(null);
 const searchInputRef = useRef<HTMLInputElement | null>(null);

 const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

 const filteredOptions = useMemo(() => {
 const q = query.trim().toLowerCase();
 if (!q) return options;

 return options.filter((o) => {
 const l = o.label.toLowerCase();
 const v = o.value.toLowerCase();
 return l.includes(q) || v.includes(q);
 });
 }, [options, query]);

 function closeAndBlur() {
 setOpen(false);
 setQuery("");
 onBlur();
 }

 useEffect(() => {
 if (!open) return;

 const t = window.setTimeout(() => {
 searchInputRef.current?.focus();
 }, 0);

 function handleOutsideClick(e: MouseEvent) {
 if (!rootRef.current) return;
 if (!rootRef.current.contains(e.target as Node)) {
 closeAndBlur();
 }
 }

 function handleEscape(e: KeyboardEvent) {
 if (e.key === "Escape") {
 closeAndBlur();
 }
 }

 document.addEventListener("mousedown", handleOutsideClick);
 document.addEventListener("keydown", handleEscape);

 return () => {
 window.clearTimeout(t);
 document.removeEventListener("mousedown", handleOutsideClick);
 document.removeEventListener("keydown", handleEscape);
 };
 // eslint-disable-next-line react-hooks/exhaustive-deps -- closeAndBlur only uses stable state setters and onBlur which is already in deps
 }, [open, onBlur]);

 function handleSelect(nextValue: string) {
 onChange(nextValue);
 setOpen(false);
 setQuery("");
 }

 return (
 <div ref={rootRef} data-searchable-select={open ? true : undefined} className="relative flex flex-col gap-1">
 <div className="flex flex-col gap-1">
 <label htmlFor={id} className="text-[17px] font-medium text-left label-text text-grey">
 {label}
 {required ? <span className="text-red"> *</span> : null}
 </label>
 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 </div>

 {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-invalid on a combobox trigger button is intentional for form validation UX */}
 <button
 id={id}
 type="button"
 name={name}
 onClick={() => {
 if (open) {
 closeAndBlur();
 } else {
 setOpen(true);
 }
 }}
 aria-haspopup="listbox"
 aria-expanded={open}
 aria-invalid={Boolean(errMsg)}
 aria-describedby={errMsg ? errorId : undefined}
 className="hs-searchable-select-trigger flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm"
 >
 <span className={selected ? "text-white opacity-100" : "text-grey-700 opacity-100"}>
 {selected?.label || "Select an option"}
 </span>
 <span
 aria-hidden="true"
 className={`hs-searchable-select-arrow inline-flex origin-center transition-transform duration-200 ${
 open ? "rotate-180" : ""
 }`}
 >
 <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
 <path d="M3 5.5L8 10.5L13 5.5" stroke="currentColor" strokeWidth="1.5" />
 </svg>
 </span>
 </button>

 {open ? (
 <div className="hs-searchable-select-panel absolute left-0 right-0 top-full z-50 mt-2 rounded-md p-2">
 <input
 ref={searchInputRef}
 type="text"
 value={query}
 onChange={(e) => setQuery(e.target.value)}
 placeholder="Search"
 className="hs-searchable-select-search mb-2 w-full rounded-md px-3 py-2 text-sm"
 />

 <div role="listbox" aria-labelledby={id} className="hs-searchable-select-options max-h-60 overflow-auto">
 {!required ? (
 <button
 type="button"
 className="hs-searchable-select-option block w-full rounded px-2 py-2 text-left text-sm"
 onClick={() => handleSelect("")}
 >
 Clear selection
 </button>
 ) : null}

 {filteredOptions.length > 0 ? (
 filteredOptions.map((option) => {
 const isSelected = option.value === value;

 return (
 <button
 key={option.value || option.label}
 type="button"
 role="option"
 aria-selected={isSelected}
 className="hs-searchable-select-option block w-full rounded px-2 py-2 text-left text-sm"
 onClick={() => handleSelect(option.value)}
 >
 {option.label}
 </button>
 );
 })
 ) : (
 <div className="hs-searchable-select-empty px-2 py-2 text-sm">No results found</div>
 )}
 </div>
 </div>
 ) : null}

 {errMsg ? (
 <p id={errorId} className="text-xs text-red error-text text-left">
 {errMsg}
 </p>
 ) : null}
 </div>
 );
}

async function trackFormEvent(
 name: "demo.started" | "demo.requested" | "newsletter.subscribed",
 properties?: Record<string, string>,
) {
 const page_path = window.location.pathname;
 try {
  const posthog = await ensurePostHogInitialized();
  posthog.capture(name, { page_path, ...properties }, { send_instantly: true, transport: "sendBeacon" });
 } catch { /* Analytics must never interrupt form submission. */ }
}

function HubspotApiFormClientInner({
 embed,
 pageUri,
 pageName,
 hiddenFieldValues,
 className,
 choiceCards,
 onSuccess,
 afterSubmitButton,
 form,
}: InnerProps) {
 const demoStarted = useRef(false);

 function startDemo() {
  const demoForm = DEMO_FORMS[form._id];
  if (!demoForm || demoStarted.current) return;
  demoStarted.current = true;
  void trackFormEvent("demo.started", { form: demoForm });
 }

 const heading = cleanString(embed.heading ?? form.presentation?.heading) || undefined;
 const intro = cleanString(embed.intro ?? form.presentation?.intro) || undefined;
 const isStartupProgrammeForm = className?.includes("startup-programme-form");
 const formatStartupIntro = (paragraph: string, paragraphIndex: number) => (
   <p key={paragraphIndex} className="m-0 mb-4 text-sm font-normal leading-normal text-grey last:mb-0">
     {paragraph.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) =>
       part.startsWith("**") && part.endsWith("**")
         ? <strong key={index}>{part.slice(2, -2)}</strong>
         : <span key={index}>{part}</span>
     )}
   </p>
 );
 const introContent = isStartupProgrammeForm && intro
   ? intro.split(/\n\s*\n/).map(formatStartupIntro)
   : intro;

 const visibleFields = useMemo(() => (form.syncedFields ?? []).filter(shouldRenderField), [form.syncedFields]);

 const blockedDomains = useMemo(
 () => normalizeBlockedDomains(form.spam?.blockedEmailDomains),
 [form.spam?.blockedEmailDomains]
 );

 const [geoCountry, setGeoCountry] = useState<string | null>(null);
 const [isGeoLoading, setIsGeoLoading] = useState(false);

 const needsGeo = useMemo(
 () =>
  (form.syncedFields ?? []).some(
  (f) =>
   f.valueSource?.type === "computed" &&
   cleanString((f.valueSource as any).computedKey)?.toLowerCase() === "country"
  ),
 [form.syncedFields]
 );

 useEffect(() => {
 if (!needsGeo) return;
 setIsGeoLoading(true);
 fetch("/api/geo")
  .then((r) => r.json())
  .then((data) => { if (data?.countryName) setGeoCountry(data.countryName); })
  .catch((err) => { console.error("[geo] Failed to detect country:", err); })
  .finally(() => { setIsGeoLoading(false); });
 }, [needsGeo]);

 const initialValues = useMemo(() => {
 const init: Record<string, any> = {};
 for (const f of visibleFields) {
 const key = cleanString(f.name);
 if (!key) continue;

 if (f.type === "checkbox") {
  init[key] = false;
 } else if (f.type === "multicheckbox") {
  init[key] = [];
 } else if (f.valueSource?.type === "computed" && f.valueSource.computedKey) {
  // country is resolved async via geoCountry; language/referrer are sync
  init[key] = getComputedValue(cleanString(f.valueSource.computedKey), null);
 } else {
  init[key] = "";
 }
 }
 return init;
 }, [visibleFields]);

 const [values, setValues] = useState<Record<string, any>>(initialValues);
 const formRenderedAt = useRef(Date.now());
 const [touched, setTouched] = useState<Record<string, boolean>>({});
 const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
 const [meetingRedirectUrl, setMeetingRedirectUrl] = useState<string | null>(null);

 useEffect(() => {
 setValues((prev) => {
 const next: Record<string, any> = { ...initialValues };
 for (const k of Object.keys(next)) {
 if (k in prev) next[k] = prev[k];
 }
 return next;
 });

 setTouched((prev) => {
 const next: Record<string, boolean> = {};
 for (const k of Object.keys(initialValues)) next[k] = Boolean(prev[k]);
 return next;
 });

 setFieldErrors((prev) => {
 const next: Record<string, string | null> = {};
 for (const k of Object.keys(initialValues)) next[k] = prev[k] ?? null;
 return next;
 });
 }, [initialValues]);

 // Once geo resolves, patch untouched computed=country fields
 useEffect(() => {
 if (!geoCountry) return;
 setValues((prev) => {
  const next = { ...prev };
  for (const f of visibleFields) {
  if (f.valueSource?.type !== "computed") continue;
  if (cleanString(f.valueSource.computedKey)?.toLowerCase() !== "country") continue;
  const key = cleanString(f.name);
  if (!key || prev[key]) continue; // skip if user already set a value
  next[key] = geoCountry;
  }
  return next;
 });
 }, [geoCountry, visibleFields]);

 const [hp, setHp] = useState("");
 const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
 const turnstileEnabled = form.turnstileEnabled === true;
 const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
 const turnstileWidgetId = useRef<string | null>(null);
 const [submitState, setSubmitState] = useState<SubmitState>("idle");
 const [errorMsg, setErrorMsg] = useState<string | null>(null);

 const submitButton = form.submitButton ?? { label: "Submit" };
 const success = form.success ?? { mode: "message", message: "Thanks! We received your submission." };

 const successMode = cleanString((success as any)?.mode).toLowerCase();
 const successRedirectUrl = cleanString((success as any)?.redirectUrl);
 const successMessage = cleanString((success as any)?.message);
 const successCtaLabel = cleanString((success as any)?.ctaLabel);
 const successCtaHref = cleanString((success as any)?.ctaHref);
 const meetingBehavior = cleanString((success as any)?.meetingBehavior).toLowerCase() || "cta";
 const meetingCtaLabel = cleanString((success as any)?.meetingCtaLabel) || "Book a meeting";
 const meetingCtaOpenInNewTab = Boolean((success as any)?.meetingCtaOpenInNewTab);

 // Keep controls unique when the same form appears in multiple page sections.
 const instanceId = useId();
 const formIdBase = `${sanitizeDomId(form._id ?? "form")}-${instanceId}`;

 const formDomId = useMemo(() => {
 const anchor = embed.anchorId ? sanitizeDomId(cleanString(embed.anchorId)) : "";
 return anchor ? `hs-form-${anchor}` : `hs-form-${formIdBase}`;
 }, [embed.anchorId, formIdBase]);

 const requiredMissing = useMemo(() => {
 const missing: string[] = [];
 for (const f of visibleFields) {
 if (!f.required) continue;

 const key = cleanString(f.name);
 if (!key) continue;

 const v = values[key];

 if (f.type === "checkbox") {
 if (!v) missing.push(key);
 } else if (f.type === "multicheckbox") {
 if (!Array.isArray(v) || v.length === 0) missing.push(key);
 } else {
 if (v == null || String(v).trim() === "") missing.push(key);
 }
 }
 return missing;
 }, [visibleFields, values]);

 const isSingleEmailForm =
 visibleFields.length === 1 &&
 (visibleFields[0]?.type === "email" || cleanString(visibleFields[0]?.name).toLowerCase() === "email");

 const hasAnyErrors = useMemo(() => Object.values(fieldErrors).some(Boolean), [fieldErrors]);
 const canSubmit = submitState !== "submitting" && requiredMissing.length === 0 && !hasAnyErrors && !isGeoLoading;

 const resetTurnstile = (message?: string) => {
  setTurnstileToken(null);
  if (message) setErrorMsg(message);
  if (turnstileWidgetId.current && window.turnstile) {
   window.turnstile.reset(turnstileWidgetId.current);
  }
 };

 useEffect(() => {
  const sitekey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  if (!turnstileEnabled || !sitekey || !turnstileContainerRef.current) return;

  const renderWidget = () => {
   if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetId.current) return;
   turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
   sitekey,
    action: "hubspot-submit",
    theme: "dark",
    appearance: process.env.NODE_ENV === "production" ? "interaction-only" : "always",
    callback: (token) => {
     setTurnstileToken(token);
     setErrorMsg(null);
    },
    "expired-callback": () => resetTurnstile("Please complete the security verification again to continue."),
    "error-callback": () => {
     setTurnstileToken(null);
     setErrorMsg("The security verification could not be completed. Please try again.");
    },
   });
  };

  if (window.turnstile) {
   renderWidget();
   return;
  }

  const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
  const script = existingScript ?? document.createElement("script");
  if (!existingScript) {
   script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
   script.async = true;
   script.defer = true;
   document.head.appendChild(script);
  }
  script.addEventListener("load", renderWidget, { once: true });

  return () => script.removeEventListener("load", renderWidget);
 }, [turnstileEnabled]);

 function setValueAndMaybeValidate(key: string, value: any, field: HubspotSyncedField) {
 startDemo();
 setValues((v) => ({ ...v, [key]: value }));

 if (touched[key]) {
 const err = validateField(field, value);
 setFieldErrors((e) => ({ ...e, [key]: err }));
 }
 }

 function touchAndValidate(key: string, field: HubspotSyncedField) {
 setTouched((t) => ({ ...t, [key]: true }));
 const err = validateField(field, values[key]);
 setFieldErrors((e) => ({ ...e, [key]: err }));
 }

 function validateAllFields(): boolean {
 const nextTouched: Record<string, boolean> = {};
 const nextErrors: Record<string, string | null> = {};

 for (const f of visibleFields) {
 const key = cleanString(f.name);
 if (!key) continue;
 nextTouched[key] = true;
 nextErrors[key] = validateField(f, values[key]);
 }

 setTouched((prev) => ({ ...prev, ...nextTouched }));
 setFieldErrors((prev) => ({ ...prev, ...nextErrors }));

 return !Object.values(nextErrors).some(Boolean);
 }

 async function onSubmit(e: React.FormEvent) {
 e.preventDefault();
 startDemo();
 setErrorMsg(null);

 const ok = validateAllFields();
 if (!ok) {
 setErrorMsg("Please fix the highlighted fields.");
 return;
 }

 const emailValue = String(values["email"] ?? "").trim();
 if (blockedDomains.length > 0 && emailValue) {
 const domain = extractEmailDomain(emailValue);
 if (domain && isDomainBlocked(domain, blockedDomains)) {
 setErrorMsg("Please use a different email address.");
 return;
 }
 }

 setSubmitState("submitting");

 if (turnstileEnabled && !turnstileToken) {
  setSubmitState("error");
  resetTurnstile("Please complete the security verification and try again.");
  return;
 }

 const hutk = getCookieValue("hubspotutk");

 // Inject values for hidden fields (default and computed valueSources)
 const hiddenDefaults: Record<string, string> = {};
 for (const f of form.syncedFields ?? []) {
 if (!f.hidden && f.ui?.enabled !== false) continue;
 const key = cleanString(f.name);
 if (!key) continue;

 if (f.valueSource?.type === "default") {
  const val = cleanString((f.valueSource as any).defaultValue);
  if (val) hiddenDefaults[key] = val;
 } else if (f.valueSource?.type === "computed" && f.valueSource.computedKey) {
  const val = getComputedValue(cleanString(f.valueSource.computedKey), geoCountry);
  if (val) hiddenDefaults[key] = val;
 }
 }

 // Page-level injections (e.g. the event a landing page belongs to) override
 // whatever the field's value source in the form definition produced.
 const injectedHidden: Record<string, string> = {};
 for (const [rawKey, rawValue] of Object.entries(hiddenFieldValues ?? {})) {
 const key = cleanString(rawKey);
 if (!key) continue;

 const field = (form.syncedFields ?? []).find(
  (f) => cleanString(f.name) === key
 );
 if (!field) continue;
 if (!field.hidden && field.ui?.enabled !== false) continue;

 const val = cleanString(rawValue == null ? "" : String(rawValue));
 if (val) injectedHidden[key] = val;
 }

 const payload: HubspotApiSubmitPayload = {
 formId: form.hubspot.formId,
 turnstileToken: turnstileEnabled ? turnstileToken ?? undefined : undefined,
 renderedAt: formRenderedAt.current,
 pageUri: pageUri ?? (typeof window !== "undefined" ? window.location.href : undefined),
 pageName: pageName ?? (typeof document !== "undefined" ? document.title : undefined),
 hutk,
 fields: { ...hiddenDefaults, ...values, ...injectedHidden },
 hp,
 };

 try {
 const res = await fetch("/api/hubspot/submit", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify(payload),
 });

 const text = await res.text();
 if (!res.ok) {
  let message = "Something went wrong. Please try again.";
  try {
   const errorBody = JSON.parse(text);
   if (res.status === 409) {
    message = "This email has already been registered for this form.";
   } else if (typeof errorBody?.error === "string" && errorBody.error.trim()) {
    message = errorBody.error;
   }
  } catch {
   // Keep the user-facing fallback when the API does not return JSON.
  }
  throw new Error(message);
 }

 // Capture accepted submissions before either success redirect can unload the page.
 // The API silently accepts honeypot submissions without sending them to HubSpot.
 if (!hp) {
  const demoRequest = getDemoRequestProperties(form._id, form.syncedFields, payload.fields);
  if (demoRequest) {
   for (const property of demoRequest.rejected) {
    // Never send the rejected value itself; it is unvalidated input.
    try {
     sendEvent("analytics.property_rejected", {
      severity: "WARN",
      attributes: { event: "demo.requested", property, form_id: form._id },
     });
    } catch { /* Telemetry must never interrupt form submission. */ }
   }
   await trackFormEvent("demo.requested", demoRequest.properties);
  }
  if (form._id === NEWSLETTER_FORM_KEY) await trackFormEvent("newsletter.subscribed");
 }

 setTurnstileToken(null);
 if (turnstileWidgetId.current && window.turnstile) window.turnstile.reset(turnstileWidgetId.current);

 let result: any = null;
 try {
 result = JSON.parse(text);
 } catch {
 result = null;
 }

 const returnedRedirectUrl =
 typeof result?.redirectUrl === "string" && result.redirectUrl.trim().length > 0
 ? result.redirectUrl.trim()
 : null;

 setMeetingRedirectUrl(returnedRedirectUrl);

 if (successMode === "redirect" && successRedirectUrl) {
 if (successRedirectUrl.startsWith("https://") || successRedirectUrl.startsWith("http://") || successRedirectUrl.startsWith("/")) {
 window.location.assign(successRedirectUrl);
 return;
 }
 }

 if (successMode === "meeting" && meetingBehavior === "redirect" && returnedRedirectUrl) {
 if (returnedRedirectUrl.startsWith("https://") || returnedRedirectUrl.startsWith("http://") || returnedRedirectUrl.startsWith("/")) {
 window.location.assign(returnedRedirectUrl);
 return;
 }
 }

 setSubmitState("success");
 onSuccess?.(payload.fields);
 } catch (err) {
  setTurnstileToken(null);
  if (turnstileWidgetId.current && window.turnstile) window.turnstile.reset(turnstileWidgetId.current);
  setSubmitState("error");
  setErrorMsg(err instanceof Error ? err.message : "Something went wrong. Please try again.");
 }
 }

 function handleMeetingCtaClick() {
 if (!meetingCtaOpenInNewTab) return;

 window.setTimeout(() => {
 window.location.reload();
 }, 150);
 }

 function renderField(field: HubspotSyncedField) {
 const key = cleanString(field.name);
 if (!key) return null;

 const normalizedKey = key.toLowerCase();
 const inlineNameFieldClass =
 normalizedKey === "firstname" || normalizedKey === "lastname" ? "hs-book-demo-inline-name-field" : "";

 const fieldNameSafe = sanitizeDomId(key);
 const id = `hsf-${formIdBase}-${fieldNameSafe}`;
 const errorId = `${id}-error`;

 const label = stripHtmlTags(decodeHtmlEntities(cleanString(field.ui?.labelOverride || field.label || key)));
 const basePlaceholder = cleanString(field.ui?.placeholderOverride || field.placeholder || "");
 const helpText = cleanString(field.ui?.helpText || "");

 const showLabel = field.ui?.labelMode === "labelAndPlaceholder";

 const placeholder =
 !showLabel && field.required
 ? (basePlaceholder || label).endsWith("*")
 ? basePlaceholder || label
 : `${basePlaceholder || label}*`
 : basePlaceholder;

 const errMsg = fieldErrors[key];
 const ariaInvalid = Boolean(errMsg);

 const common = {
 id,
 name: key,
 required: Boolean(field.required),
 "aria-required": Boolean(field.required),
 "aria-label": showLabel ? undefined : label,
 "aria-invalid": ariaInvalid,
 "aria-describedby": ariaInvalid ? errorId : undefined,
 } as const;

 const fieldError = errMsg ? (
 <p id={errorId} className="text-xs text-red error-text text-left">
 {errMsg}
 </p>
 ) : null;

 if (field.type === "textarea") {
 return (
 <div key={key} className="flex flex-col gap-1">
 {showLabel ? (
 <div className="flex flex-col gap-1">
 <label htmlFor={id} className="text-[17px] font-medium text-left label-text text-grey">
 {label}
 {field.required ? <span className="text-red"> *</span> : null}
 </label>
 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 </div>
 ) : null}

 <textarea
 {...common}
 placeholder={placeholder || undefined}
 value={String(values[key] ?? "")}
 onChange={(e) => setValueAndMaybeValidate(key, e.target.value, field)}
 onBlur={() => touchAndValidate(key, field)}
 className="w-full rounded-md border px-3 py-2 text-sm"
 rows={4}
 />

 {fieldError}
 </div>
 );
 }

 if (field.type === "select") {
 const options = (field.options ?? []).map((o) => ({
 value: decodeHtmlEntities(cleanString(o.value)),
 label: decodeHtmlEntities(cleanString(o.label || o.value)),
 }));

 return (
 <SearchableSelectField
 key={key}
 id={id}
 name={key}
 label={label}
 required={Boolean(field.required)}
 value={String(values[key] ?? "")}
 options={options}
 helpText={helpText}
 errMsg={errMsg}
 errorId={errorId}
 onChange={(nextValue) => {
 startDemo();
 setValues((v) => ({ ...v, [key]: nextValue }));
 setTouched((t) => ({ ...t, [key]: true }));
 const err = validateField(field, nextValue);
 setFieldErrors((e) => ({ ...e, [key]: err }));
 }}
 onBlur={() => touchAndValidate(key, field)}
 />
 );
 }

 if (field.type === "radio") {
 return (
 <div key={key} className="flex flex-col gap-2">
 <div className="flex flex-col gap-1">
 <div className="text-[17px] font-medium text-left label-text text-grey">
 {label}
 {field.required ? <span className="text-red"> *</span> : null}
 </div>
 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 </div>

 <div className="flex flex-col gap-2">
 {(field.options ?? []).map((o) => {
 const ov = cleanString(o.value);
 const ol = cleanString(o.label);
 const rid = `${id}-${sanitizeDomId(ov || ol)}`;

 return (
 <label key={ov || ol} htmlFor={rid} className="flex items-center gap-2 text-sm">
 {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- aria-invalid on radio inputs is valid ARIA but incorrectly flagged by jsx-a11y */}
 <input
 id={rid}
 type="radio"
 name={key}
 value={ov}
 checked={String(values[key] ?? "") === ov}
 onChange={(e) => setValueAndMaybeValidate(key, e.target.value, field)}
 onBlur={() => touchAndValidate(key, field)}
 aria-invalid={ariaInvalid}
 aria-describedby={ariaInvalid ? errorId : undefined}
 />
 <span>{ol || ov}</span>
 </label>
 );
 })}
 </div>

 {fieldError}
 </div>
 );
 }

 if (field.type === "multicheckbox") {
 const arr: string[] = Array.isArray(values[key]) ? values[key] : [];

 if (choiceCards?.[key]) {
 return (
 <fieldset key={key} className="flex flex-col gap-3">
 <legend className="sr-only">{label}{field.required ? " (required)" : ""}</legend>
 {(field.options ?? []).map((option) => {
 const value = cleanString(option.value);
 const checked = arr.includes(value);
 const copy = choiceCards[key][value];
 return (
 <CheckboxCard
 key={value}
 name={key}
 value={value}
 label={copy?.label ?? option.label ?? value}
 description={copy?.description}
 checked={checked}
 disabled={option.disabled}
 onChange={() => setValueAndMaybeValidate(key, checked ? arr.filter(item => item !== value) : [...arr, value], field)}
 onBlur={() => touchAndValidate(key, field)}
 aria-invalid={ariaInvalid}
 aria-describedby={ariaInvalid ? errorId : undefined}
 />
 );
 })}
 {fieldError}
 </fieldset>
 );
 }

 return (
 <div key={key} className="flex flex-col gap-2">
 <div className="flex flex-col gap-1">
 <div className="text-[17px] font-medium text-left label-text text-grey">
 {label}
 {field.required ? <span className="text-red"> *</span> : null}
 </div>
 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 </div>

 <div className="flex flex-col gap-2">
 {(field.options ?? []).map((o) => {
 const ov = cleanString(o.value);
 const ol = cleanString(o.label);
 const isDisabled = Boolean(o.disabled);
 const displayLabel = isDisabled && o.labelOverride ? cleanString(o.labelOverride) : (ol || ov);
 const cid = `${id}-${sanitizeDomId(ov || ol)}`;
 const checked = arr.includes(ov);

 return (
 <label key={ov || ol} htmlFor={cid} className={`flex items-center gap-2 text-sm${isDisabled ? " opacity-50 cursor-not-allowed" : ""}`}>
 <input
 id={cid}
 type="checkbox"
 checked={checked}
 disabled={isDisabled}
 onChange={() => {
 if (isDisabled) return;
 const next = checked ? arr.filter((x) => x !== ov) : [...arr, ov];
 setValueAndMaybeValidate(key, next, field);
 }}
 onBlur={() => touchAndValidate(key, field)}
 aria-invalid={ariaInvalid}
 aria-describedby={ariaInvalid ? errorId : undefined}
 />
 <span>{displayLabel}</span>
 </label>
 );
 })}
 </div>

 {fieldError}
 </div>
 );
 }

 if (field.type === "checkbox") {
 return (
 <div key={key} className="flex flex-col gap-1">
 <label htmlFor={id} className="flex items-center gap-2 text-sm">
 <input
 id={id}
 type="checkbox"
 checked={Boolean(values[key])}
 onChange={(e) => setValueAndMaybeValidate(key, e.target.checked, field)}
 onBlur={() => touchAndValidate(key, field)}
 required={Boolean(field.required)}
 aria-invalid={ariaInvalid}
 aria-describedby={ariaInvalid ? errorId : undefined}
 />
 <span>
 {label}
 {field.required ? <span className="text-red"> *</span> : null}
 </span>
 </label>

 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 {fieldError}
 </div>
 );
 }

 const inputType =
 field.type === "email"
 ? "email"
 : field.type === "phone"
 ? "tel"
 : field.type === "number"
 ? "number"
 : field.type === "date"
 ? "date"
 : field.type === "datetime"
 ? "datetime-local"
 : "text";

 return (
 <div key={key} className={["flex flex-col gap-1", inlineNameFieldClass].filter(Boolean).join(" ")}>
 {showLabel ? (
 <div className="flex flex-col gap-1">
 <label htmlFor={id} className="text-[17px] font-medium text-left label-text text-grey">
 {label}
 {field.required ? <span className="text-red"> *</span> : null}
 </label>
 {helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 </div>
 ) : null}

 {isStartupProgrammeForm && field.type === "number" ? (
 <div className="relative">
 <input
 {...common}
 type={inputType}
 placeholder={placeholder || undefined}
 value={String(values[key] ?? "")}
 onChange={(e) => setValueAndMaybeValidate(key, e.target.value, field)}
 onBlur={() => touchAndValidate(key, field)}
 className="w-full rounded-md border px-3 py-2 pr-10 text-sm"
 />
 <div className="absolute inset-y-0 right-0 flex w-8 flex-col border-l border-grey-850">
 <button
 type="button"
 aria-label={`Increase ${label}`}
 className="flex flex-1 items-center justify-center text-xs text-grey-700 hover:text-grey"
 onClick={() => setValueAndMaybeValidate(key, String(Number(values[key] || 0) + 1), field)}
 >
 <ChevronUp size={18} strokeWidth={2} />
 </button>
 <button
 type="button"
 aria-label={`Decrease ${label}`}
 className="flex flex-1 items-center justify-center text-xs text-grey-700 hover:text-grey"
 onClick={() => setValueAndMaybeValidate(key, String(Math.max(0, Number(values[key] || 0) - 1)), field)}
 >
 <ChevronDown size={18} strokeWidth={2} />
 </button>
 </div>
 </div>
 ) : (
 <input
 {...common}
 type={inputType}
 placeholder={placeholder || undefined}
 value={String(values[key] ?? "")}
 onChange={(e) => setValueAndMaybeValidate(key, e.target.value, field)}
 onBlur={() => touchAndValidate(key, field)}
 className="w-full rounded-md border px-3 py-2 text-sm"
 />
 )}

 {!showLabel && helpText ? (
 <p className="text-sm opacity-100 help-text text-left text-grey mb-3">{helpText}</p>
 ) : null}
 {fieldError}
 </div>
 );
 }

 const buttonWidthMode = cleanString(submitButton.width ?? "full");
 const buttonAlign = cleanString(submitButton.align ?? "left");
 const customWidth = cleanString(submitButton.customWidth ?? "");

 const effectiveButtonWidthMode = isSingleEmailForm ? "auto" : buttonWidthMode;
 const effectiveButtonAlign = isSingleEmailForm ? "right" : buttonAlign;

 const buttonWrapOuterClass = useMemo(() => {
 const align = effectiveButtonWidthMode === "full" ? "left" : effectiveButtonAlign;

 if (align === "center") return "w-full flex justify-center";
 if (align === "right") return "w-full flex justify-end";
 return "w-full flex justify-start";
 }, [effectiveButtonAlign, effectiveButtonWidthMode]);

 const buttonWrapInnerStyle: React.CSSProperties = useMemo(() => {
 if (effectiveButtonWidthMode === "full") return { width: "100%" };
 if (effectiveButtonWidthMode === "custom" && customWidth) {
 return { width: customWidth, maxWidth: "100%" };
 }
 return { width: "auto", maxWidth: "100%" };
 }, [effectiveButtonWidthMode, customWidth]);

 const buttonClassName = useMemo(() => {
 const fill = effectiveButtonWidthMode === "full" || effectiveButtonWidthMode === "custom";
 return [
 "rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50",
 fill ? "w-full!" : "",
 isSingleEmailForm ? "h-full" : "",
 ]
 .filter(Boolean)
 .join(" ");
 }, [effectiveButtonWidthMode, isSingleEmailForm]);

 const privacy = form.privacyNotice;

 const showPrivacy =
 privacy?.enabled !== false &&
 Array.isArray(privacy?.content) &&
 privacy.content.length > 0;

 const privacyPlacement = normalizePrivacyPlacement(privacy?.placement);

 const privacyBlock = showPrivacy ? (
 <div className="text-[12px] opacity-100 text-left text-grey mt-4 privacy-text">
 <PortableText
 value={privacy.content!}
 components={{
 block: {
 normal: ({ children }) => <p className="text-[12px]">{children}</p>,
 },
 marks: {
 strong: ({ children }) => <strong>{children}</strong>,
 em: ({ children }) => <em>{children}</em>,
 link: ({ value, children }) => (
 <a
 href={value?.href}
 className="underline"
 {...(value?.isExternal ? { target: "_blank", rel: "noreferrer" } : {})}
 >
 {children}
 </a>
 ),
 },
 }}
 />
 </div>
 ) : null;

 if (
 submitState === "success" &&
 (successMode === "message" || (successMode === "meeting" && meetingBehavior === "cta"))
 ) {
 return (
 <div id={formDomId} className={["rounded-xl border p-6", isStartupProgrammeForm ? "flex min-h-[180px] flex-col items-center justify-center pb-10 text-center" : "", className].filter(Boolean).join(" ")}>
 {heading ? <h3 className="text-lg font-semibold form-header">{heading}</h3> : null}

 <p className={isStartupProgrammeForm ? "m-0 max-w-md text-center text-sm md:text-base message-text" : "mt-2 text-sm message-text"}>
 {successMessage || "Thanks! We received your submission."}
 </p>
 {isStartupProgrammeForm && successCtaLabel && successCtaHref ? (
 <a href={successCtaHref} className="mt-5 text-sm font-medium text-red underline underline-offset-4 hover:opacity-80">
 {successCtaLabel}
 </a>
 ) : null}

 {successMode === "meeting" && meetingBehavior === "cta" && meetingRedirectUrl ? (
 <a
 href={meetingRedirectUrl}
 target={meetingCtaOpenInNewTab ? "_blank" : undefined}
 rel={meetingCtaOpenInNewTab ? "noreferrer" : undefined}
 onClick={handleMeetingCtaClick}
 className="hs-form-meeting-cta inline-flex items-center justify-center mt-4 rounded-md border px-4 py-2 text-sm font-medium"
 >
 {meetingCtaLabel}
 </a>
 ) : null}
 </div>
 );
 }

 return (
 <div>
 <form id={formDomId} onSubmit={onSubmit} className={["rounded-xl border p-6", className].filter(Boolean).join(" ")}>
 {heading ? <h3 className="text-lg font-semibold form-header">{heading}</h3> : null}
 {intro ? (
   isStartupProgrammeForm
     ? <div className="mt-2 text-left text-sm opacity-100 text-grey intro-text">{introContent}</div>
     : <p className="mt-2 text-sm opacity-100 text-grey intro-text">{introContent}</p>
 ) : null}

 <div className="mt-5 flex flex-col gap-4 form-inner-div">
 <input
 type="text"
 name="hp"
 value={hp}
 onChange={(e) => setHp(e.target.value)}
 tabIndex={-1}
 autoComplete="off"
 className="hidden"
 />

 {isSingleEmailForm ? (
 <>
 {privacyPlacement === "aboveButton" ? privacyBlock : null}

 <div className="flex w-full items-stretch gap-3">
 <div className="flex-1">{renderField(visibleFields[0])}</div>
 <div className="shrink-0">
 <button type="submit" disabled={!canSubmit} className={buttonClassName}>
 {submitState === "submitting" ? submitButton.loadingLabel ?? "Submitting…" : submitButton.label ?? "Submit"}
 </button>
 </div>
 </div>

 {afterSubmitButton}

 {privacyPlacement === "belowButton" ? privacyBlock : null}
 </>
 ) : (
 <>
 {visibleFields.map(renderField)}

 {privacyPlacement === "aboveButton" ? privacyBlock : null}

 {turnstileEnabled && process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ? (
  <div ref={!isSingleEmailForm ? turnstileContainerRef : undefined} className="flex justify-start cf-turnstile" aria-label="Security verification" />
 ) : null}

 <div className={buttonWrapOuterClass}>
 <div style={buttonWrapInnerStyle}>
 <button type="submit" disabled={!canSubmit} className={buttonClassName}>
 {submitState === "submitting" ? submitButton.loadingLabel ?? "Submitting…" : submitButton.label ?? "Submit"}
 </button>
 </div>
 </div>

 {afterSubmitButton}

 {privacyPlacement === "belowButton" ? privacyBlock : null}
 </>
 )}

 {errorMsg ? <p className="text-sm text-center text-red">{errorMsg}</p> : null}
 </div>
 </form>
 {turnstileEnabled && process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && isSingleEmailForm ? (
  <div ref={turnstileContainerRef} className="mt-3 flex justify-start cf-turnstile" aria-label="Security verification" />
 ) : null}
 </div>
 );
}
